import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Prose from '../components/Prose';
import Reveal from '../components/Reveal';
import SiteImage from '../components/SiteImage';
import Spinner from '../components/Spinner';
import { api, type Page } from '../lib/api';

/** Public brochure images, converted from the print .tif files: a full-size and an 800px copy. */
const MEDIA = '/media/intro';

const QUOTES = [
  {
    author: 'Watchman Nee',
    source: 'Watchman Nee—A Seer of the Divine Revelation in the Present Age',
    image: { name: 'watchman-nee', width: 900, height: 1256, fade: false },
    text: 'If the return of the Lord should be delayed, it will be necessary to raise up a number of young people to continue the testimony and the work for the following generations.',
  },
  {
    author: 'Witness Lee',
    source: 'Elders’ Training—Book 9—The Eldership and the God-ordained Way (1)',
    image: { name: 'witness-lee', width: 1200, height: 714, fade: true },
    text: 'We do have an expectation that many of the young college graduates among us will spend either one or two years of time to learn something of the Lord, that is, to learn the Word and to learn the lessons in life.',
  },
];

/**
 * The homepage, after the printed "An Introduction to FTTMY": a cover, the two
 * portrait quotes side by side, the Introduction text from the CMS, and a
 * closing quotation. Photos have soft faded edges like the print; blocks fade
 * up once as they scroll in. The announcement sits in its own bar above this.
 */
export default function Home() {
  const [page, setPage] = useState<Page | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api<{ page: Page }>('/pages/introduction')
      .then((r) => !cancelled && setPage(r.page))
      .catch(() => !cancelled && setPage(null))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const related = [
    { to: '/burden', title: 'Our Burden' },
    { to: '/purpose-goal', title: 'Purpose & Goal' },
    { to: '/an-open-letter', title: 'An Open Letter' },
    { to: '/courses', title: 'Courses' },
  ];

  return (
    <>
      {/* -------------------------------- cover -------------------------------- */}
      {/* Like the printed cover: the building fills the band under a pale wash, with
          the title centred on top. The wash also hides the softness of the photo
          being enlarged on wide screens. */}
      <section
        aria-labelledby="cover-heading"
        className="relative isolate grid h-72 place-items-center overflow-hidden px-4 sm:h-96 sm:px-6 lg:h-[30rem]"
      >
        <SiteImage
          src={`${MEDIA}/cover-building`}
          width={1238}
          height={1282}
          alt="The FTTMY training centre"
          sizes="100vw"
          priority
          className="absolute inset-0 -z-10 h-full w-full object-cover mask-b-from-70%"
        />
        <div aria-hidden className="absolute inset-0 -z-10 bg-radial from-white/85 via-white/65 to-white/40" />
        {/* Three rows after the brochure, close in size and in one gold, told apart
            by style alone (italic, letter-spaced, plain). A white halo keeps the
            letters clear of the photo. */}
        <h1
          id="cover-heading"
          className="text-center font-display font-bold text-gold-700 [text-shadow:0_0_2px_#fff,0_0_14px_rgb(255_255_255/0.95)]"
        >
          <span className="slide-in-down block text-2xl italic sm:text-3xl lg:text-4xl">
            An introduction to
          </span>
          <span className="slide-in-down mt-2 block text-3xl tracking-[0.08em] [text-shadow:0_0_2px_#fff,0_3px_10px_rgb(82_58_16/0.35)] [animation-delay:100ms] sm:text-4xl lg:text-5xl">
            FTTMY
          </span>
          <span className="slide-in-down mt-2 flex items-center justify-center gap-4 text-2xl [text-shadow:0_0_2px_#fff,0_3px_10px_rgb(82_58_16/0.35)] [animation-delay:200ms] sm:text-3xl lg:gap-5 lg:text-4xl">
            {/* Thin gold rules either side, like a ribbon, drawn outward from the text;
                too wide for a phone, which gets a bar below instead. */}
            <span aria-hidden className="draw-out h-0.5 w-20 origin-right rounded-full bg-linear-to-r from-gold-300/0 via-gold-400 to-gold-600 max-sm:hidden lg:w-32" />
            Full-Time Training in Malaysia
            <span aria-hidden className="draw-out h-0.5 w-20 origin-left rounded-full bg-linear-to-l from-gold-300/0 via-gold-400 to-gold-600 max-sm:hidden lg:w-32" />
          </span>
          <span
            aria-hidden
            className="draw-out mx-auto mt-4 block h-[3px] w-16 rounded-full bg-linear-to-r from-gold-600 to-gold-400 sm:hidden"
          />
        </h1>
      </section>

      {/* ---------------------------- portrait quotes ---------------------------- */}
      <section
        aria-label="Quotations"
        className="mx-auto grid w-full gap-5 px-4 py-10 sm:px-6 md:grid-cols-2 lg:gap-6 lg:px-10 lg:py-12 xl:px-14"
      >
        {QUOTES.map((q) => (
          <Reveal key={q.author}>
            <figure className="flex h-full flex-col gap-5 rounded-2xl bg-white p-6 shadow-[0_6px_28px_-6px_rgb(17_28_49/0.14)] sm:flex-row sm:items-start lg:p-8">
              {/* On phones the portrait is centred with the name under it; wider, it sits beside the quote. */}
              <div className="shrink-0 self-center text-center sm:self-start">
                {/* One portrait frame for both, so the cards match. Edges fade into the
                    card like the print; Watchman Nee's photo has the fade built in. */}
                <SiteImage
                  src={`${MEDIA}/${q.image.name}`}
                  width={q.image.width}
                  height={q.image.height}
                  alt={q.author}
                  sizes="10rem"
                  className={`h-44 w-32 object-cover object-[45%_center] lg:h-52 lg:w-40 ${q.image.fade ? 'mask-x-from-80% mask-y-from-85%' : ''}`}
                />
                <p aria-hidden className="mt-3 text-lg italic text-gold-700 sm:hidden">
                  {q.author}
                </p>
              </div>
              <div>
                <blockquote>
                  <p className="text-lg leading-relaxed text-ink-900">{q.text}</p>
                </blockquote>
                <figcaption className="mt-4 text-lg italic text-gold-700">
                  <span className="max-sm:sr-only">{q.author}</span>
                  <cite className="mt-1 block text-base sm:mt-0.5">({q.source})</cite>
                </figcaption>
              </div>
            </figure>
          </Reveal>
        ))}
      </section>

      {/* ------------------------------ introduction ------------------------------ */}
      <section aria-labelledby="intro-heading">
        <div className="mx-auto w-full px-4 pb-10 sm:px-6 lg:px-10 lg:pb-12 xl:px-14">
          {/* Marks where the reading part begins, after the cover and quotes. */}
          <hr className="h-px border-0 bg-linear-to-r from-transparent via-gold-300 to-transparent" />

          <Reveal className="pt-10 lg:pt-12">
            <h2
              id="intro-heading"
              className="font-display text-3xl font-bold tracking-tight text-gold-700 sm:text-4xl"
            >
              Introduction
            </h2>
            <div className="mt-3.5 h-[3px] w-14 rounded-full bg-linear-to-r from-gold-500 to-gold-300" />

            {loading ? (
              <Spinner label="Loading introduction" />
            ) : page ? (
              <Prose html={page.contentHtml} className="mt-5" />
            ) : (
              <p className="mt-7 text-ink-900">The introduction content is not available right now.</p>
            )}
          </Reveal>

          <Reveal className="mt-10">
            {/* Framed by gold rules, with the trainees behind it like a watermark: the
                whole photo at its own shape, centred and very faint, so the words lead. */}
            <figure className="relative isolate overflow-hidden border-y border-gold-300 px-2 py-10 text-center sm:px-10 lg:py-16">
              <SiteImage
                src={`${MEDIA}/trainees-group`}
                width={1500}
                height={578}
                alt="A group of FTTMY trainees"
                sizes="(min-width: 1024px) 56rem, 100vw"
                className="absolute left-1/2 top-1/2 -z-10 h-auto w-full max-w-4xl -translate-1/2 opacity-10 mask-x-from-70% mask-y-from-70%"
              />
              <blockquote>
                <p className="mx-auto max-w-4xl text-lg font-bold leading-relaxed text-navy-600 [text-shadow:0_0_8px_#fff]">
                  My thought is not to establish a seminary or a Bible institute, but to have young people
                  staying together to live the Body life and practice the spiritual life. In such a place
                  they would receive training for the purpose of edification, by learning to read the
                  Scripture, to pray, and to build up a good character.
                </p>
              </blockquote>
              <figcaption className="mt-4 text-lg italic text-gold-700">
                Watchman Nee
                <cite className="mt-0.5 block text-base">
                  (Watchman Nee—A Seer of the Divine Revelation in the Present Age)
                </cite>
              </figcaption>
            </figure>
          </Reveal>
        </div>
      </section>

      {/* ---------------------------- related reading ---------------------------- */}
      <section className="mx-auto w-full px-4 pb-12 pt-8 sm:px-6 lg:px-10 lg:pb-14 lg:pt-10 xl:px-14" aria-label="Read more">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {related.map((card) => (
            <Link
              key={card.to}
              to={card.to}
              /* Restrained hover: the shadow deepens and the border warms. Nothing moves. */
              className="group rounded-2xl border border-ink-100 bg-linear-to-b from-white to-sand-50/60 p-7 shadow-sm shadow-ink-900/5 transition-colors hover:border-gold-200 hover:shadow-md hover:shadow-ink-900/8"
            >
              <h2 className="font-display text-xl font-bold tracking-tight text-ink-900">
                {card.title}
              </h2>
              <span className="mt-5 inline-flex items-center gap-2.5 text-lg font-bold text-gold-600">
                Read more
                <span className="grid h-7 w-7 place-items-center rounded-full bg-gold-100 text-gold-600">
                  <svg aria-hidden viewBox="0 0 20 20" className="h-3.5 w-3.5" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M7.3 4.3 13 10l-5.7 5.7-1.4-1.4L10.2 10 5.9 5.7l1.4-1.4Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </span>
              </span>
            </Link>
          ))}
        </div>
      </section>
    </>
  );
}
